import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { StartNewEASM, StartNewPentest } from '../Header/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import Button from '.';

/** Above table rows / MUI layers so START menus are not covered by cells below */
const MENU_Z_INDEX = 13000;

const DropdownButton = ({
  components,
  title = 'START',
  color = 'bg-primary-500',
  showAddIcon = false,
  className,
  icon,
  reverseDropdown = false,
}) => {
  const [currentComponent, setCurrentComponent] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0, transform: 'none' });

  const updateMenuPosition = useCallback(() => {
    const el = dropdownRef.current;
    if (!el || !isDropdownOpen) return;
    const rect = el.getBoundingClientRect();
    if (reverseDropdown) {
      setMenuCoords({
        left: rect.left,
        top: rect.top - 8,
        transform: 'translateY(-100%)',
      });
    } else {
      setMenuCoords({
        left: rect.left,
        top: rect.bottom + 8,
        transform: 'none',
      });
    }
  }, [isDropdownOpen, reverseDropdown]);

  useLayoutEffect(() => {
    if (!isDropdownOpen) return;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [isDropdownOpen, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (components) {
      components.map((ele, index) => {
        if (ele == null) components.splice(1, index);
      });
    }
  }, [components]);

  const menuList =
    isDropdownOpen && components?.length ? (
      <ul
        ref={menuRef}
        role="menu"
        style={{
          position: 'fixed',
          top: menuCoords.top,
          left: menuCoords.left,
          transform: menuCoords.transform,
          zIndex: MENU_Z_INDEX,
        }}
        className="max-h-[min(320px,70vh)] max-w-[min(20rem,calc(100vw-2rem))] min-w-[14rem] w-max overflow-y-auto rounded-md bg-white px-3 py-1 shadow-lg"
      >
        {components.map((component, index) => (
          <li
            key={index}
            role="menuitem"
            onClick={() => {
              setCurrentComponent(index);
              setIsDropdownOpen(false);
            }}
            className={`flex w-full min-h-[2.75rem] items-center py-2 text-left text-primary-500 font-semibold text-base hover:bg-gray-200 cursor-pointer ${
              index < components.length - 1 ? 'menu-border' : ''
            }`}
          >
            {component}
          </li>
        ))}
      </ul>
    ) : null;

  return (
    <div className="relative inline-flex items-center" ref={dropdownRef}>
      <Button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`${className} w-32 px-4 py-2 ${color} text-white rounded-md flex items-center justify-center`}
      >
        {showAddIcon && <AddIcon />}
        <span className="ml-2">{title}</span>
        <span className="ml-2">
          {icon ? icon : isDropdownOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </span>
      </Button>
      {typeof document !== 'undefined' && menuList ? createPortal(menuList, document.body) : null}
    </div>
  );
};

export default DropdownButton;
